import { useState, useEffect, useRef } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";

// Text-to-Speech reader using the browser's native Web Speech API (no external service).
// Reads the content inside the nearest [data-page-reader-root] container aloud.
export default function PageReader({ title = "" }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [state, setState] = useState("idle"); // idle | playing | paused
  const chunksRef = useRef([]);
  const idxRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    // Stop any narration when the page (title) changes or on unmount.
    return () => { if (supported) window.speechSynthesis.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  if (!supported) return null;

  const collectText = () => {
    const root = document.querySelector("[data-page-reader-root]");
    if (!root) return "";
    const clone = root.cloneNode(true);
    clone.querySelectorAll("[data-reader-control]").forEach((n) => n.remove());
    return clone.innerText.replace(/[ \t]+/g, " ").trim();
  };

  const speakNext = () => {
    if (stoppedRef.current) return;
    const chunks = chunksRef.current;
    const i = idxRef.current;
    if (i >= chunks.length) { setState("idle"); idxRef.current = 0; return; }
    const u = new SpeechSynthesisUtterance(chunks[i]);
    u.rate = 1; u.pitch = 1; u.lang = document.documentElement.lang || "en-US";
    u.onend = () => { if (stoppedRef.current) return; idxRef.current = i + 1; speakNext(); };
    window.speechSynthesis.speak(u);
  };

  const play = () => {
    if (state === "paused") { window.speechSynthesis.resume(); setState("playing"); return; }
    const text = `${title}. ${collectText()}`;
    const chunks = (text.match(/[^.!?\n]+[.!?\n]*/g) || [text]).map((c) => c.trim()).filter(Boolean);
    chunksRef.current = chunks;
    idxRef.current = 0;
    stoppedRef.current = false;
    window.speechSynthesis.cancel();
    setState("playing");
    speakNext();
  };

  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const stop = () => { stoppedRef.current = true; window.speechSynthesis.cancel(); setState("idle"); idxRef.current = 0; };

  return (
    <div data-reader-control className="flex items-center gap-2 mb-8 not-prose" data-testid="page-reader">
      {state === "idle" ? (
        <button onClick={play} data-testid="reader-listen" className="inline-flex items-center gap-2 text-sm font-semibold text-brand bg-blue-50 hover:bg-blue-100 rounded-full px-4 py-2 transition-colors">
          <Volume2 size={16} /> Listen to this page
        </button>
      ) : (
        <div className="inline-flex items-center gap-1.5 bg-blue-50 rounded-full px-2 py-1.5">
          {state === "playing" ? (
            <button onClick={pause} data-testid="reader-pause" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:bg-blue-100 rounded-full px-3 py-1 transition-colors"><Pause size={15} /> Pause</button>
          ) : (
            <button onClick={play} data-testid="reader-resume" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:bg-blue-100 rounded-full px-3 py-1 transition-colors"><Play size={15} /> Resume</button>
          )}
          <button onClick={stop} data-testid="reader-stop" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 rounded-full px-3 py-1 transition-colors"><Square size={13} /> Stop</button>
          <span className="flex items-center gap-1 pl-1 pr-2">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            <span className="text-[11px] font-medium text-slate-400">Reading…</span>
          </span>
        </div>
      )}
    </div>
  );
}
